import Reveal from "@/components/Reveal";

const IMG_CIRCUIT =
  "https://images.pexels.com/photos/34924856/pexels-photo-34924856.jpeg?auto=compress&cs=tinysrgb&w=1600";
const IMG_WAFER =
  "https://images.unsplash.com/photo-1587845323226-bad89242c735?crop=entropy&cs=srgb&fm=jpg&q=85";

const CHAPTERS = [
  {
    num: "01",
    title: "The silicon bottleneck",
    body: "Every extra order of magnitude in model size now costs more in electrons than in math. Electronic compute burns energy on every operation and stalls on every clock cycle, while the math keeps outgrowing the machines that run it.",
  },
  {
    num: "02",
    title: "The photonic turn",
    body: "Light computes as it propagates — free of clocks and heat. Freeform approaches go further still: beams interfere in open space, in whatever geometry the optics allows. Interference does the linear algebra while light simply travels.",
  },
  {
    num: "03",
    title: "What we build",
    body: "QPI Laboratories, Inc. builds a simulator for photonic AI accelerators — the tool that finds the compute sweet spot before committing to hardware. The hardware and the roadmap stay behind the curtain for now; today we are building the place where you feel the physics first.",
  },
];

export default function Manifesto() {
  return (
    <section id="manifesto" className="relative py-28 md:py-40" data-testid="manifesto">
      <div className="container-x">
        <Reveal>
          <p className="eyebrow mb-4">Manifesto</p>
          <h2 className="max-w-3xl text-4xl font-semibold tracking-tighter md:text-5xl">
            What we&apos;re building, in three chapters.
          </h2>
        </Reveal>

        <div className="mt-20 md:mt-28">
          {CHAPTERS.map((chapter, i) => (
            <Reveal key={chapter.num} delay={0.05 * i}>
              <article
                className="grid grid-cols-1 gap-6 border-t border-white/10 py-12 md:grid-cols-12 md:gap-10 md:py-16"
                data-testid={`manifesto-chapter-${chapter.num}`}
              >
                <div className="md:col-span-2">
                  <span className="font-mono text-sm font-bold text-laser">{chapter.num}</span>
                </div>
                <h3 className="text-2xl font-medium tracking-tight md:col-span-4 md:text-3xl">
                  {chapter.title}
                </h3>
                <p className="max-w-xl font-light leading-relaxed text-zinc-400 md:col-span-6">
                  {chapter.body}
                </p>
              </article>
            </Reveal>
          ))}
        </div>

        <div className="mt-8 grid grid-cols-1 gap-8 border-t border-white/10 pt-16 md:grid-cols-12 md:gap-10">
          <Reveal className="md:col-span-7">
            <figure className="clip-frame overflow-hidden border border-white/10">
              <img
                src={IMG_CIRCUIT}
                alt="Macro photograph of conventional silicon chips on a circuit board"
                loading="lazy"
                className="h-72 w-full object-cover transition-transform duration-700 hover:scale-105 md:h-96"
              />
            </figure>
            <figcaption className="mt-3 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-600">
              fig. 01 — the silicon bottleneck, up close
            </figcaption>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-5">
            <figure className="clip-frame overflow-hidden border border-white/10">
              <img
                src={IMG_WAFER}
                alt="Iridescent silicon wafer with photonic dies"
                loading="lazy"
                className="h-72 w-full object-cover transition-transform duration-700 hover:scale-105 md:h-96"
              />
            </figure>
            <figcaption className="mt-3 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-600">
              fig. 02 — where light becomes hardware
            </figcaption>
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-12">
            <div
              className="clip-frame-sm flex flex-col justify-between gap-6 border border-white/10 bg-panel p-8 md:flex-row md:items-center md:p-10"
              data-testid="manifesto-teaser-card"
            >
              <div>
                <p className="eyebrow mb-3 text-beam">Status</p>
                <p className="text-xl font-light text-zinc-300 md:text-2xl">
                  The simulator is live. The full picture stays in the dark — where light behaves
                  best.
                </p>
              </div>
              <span className="shrink-0 font-mono text-[11px] uppercase tracking-[0.3em] text-laser">
                // to be continued
              </span>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
